/**
 * AdminOnlyRoute — a whole screen that belongs to the platform admin.
 *
 * Used for the template gallery, whose entire purpose is choosing or creating a
 * template. A designer landing there would only find controls that the server
 * refuses, so they get an explanation instead of a wall of dead buttons.
 *
 * This is presentation. The rule itself is enforced server-side in
 * products/utils/editing_rights.py — a designer who types the URL, or posts to
 * the endpoint directly, is refused there.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import Base from './Base';
import usePlatformAdmin from '../hooks/usePlatformAdmin';

const AdminOnlyRoute = ({ children, what = 'this screen' }) => {
  const isAdmin = usePlatformAdmin();
  if (isAdmin) return children;

  return (
    <Base Content={
      <div className="min-h-screen bg-bone p-4 sm:p-8">
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-2xl border border-line p-8 sm:p-10 text-center">
            <div className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: '#FDF3F3', color: '#B40A0A' }}>
              <Lock className="w-5 h-5" />
            </div>
            <h1 className="font-fell text-2xl font-semibold text-ink mb-2">
              Your Inikru admin looks after {what}
            </h1>
            <p className="text-[15px] text-muted mb-6">
              Your store’s design is set up and maintained for you. You can still change everything
              customers read — your words, your pictures, your colours, your prices and policies.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              <Link to="/store-content"
                className="inline-flex items-center px-5 py-2.5 rounded-full text-[14px] text-white font-semibold"
                style={{ background: 'linear-gradient(180deg, #e6783b, #B40A0A)' }}>
                Edit your content
              </Link>
              <Link to="/homepage"
                className="inline-flex items-center px-5 py-2.5 rounded-full text-[14px] border border-line text-ink hover:border-ink">
                Back to dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    } />
  );
};

export default AdminOnlyRoute;
